/**
 * Application routes constants
 * Centralized route definitions for consistent navigation
 */

export const ROUTES = {
  // Main routes — app is fully a dashboard, / redirects to /dashboard
  HOME: '/dashboard',
  PROFILE: '/dashboard/profile',

  // Dashboard routes
  DASHBOARD_PLAYGROUND: '/dashboard/playground',
  DASHBOARD_QUICKSTART: '/dashboard/quickstart',
  DASHBOARD_CHARACTERS: '/dashboard/characters',
  DASHBOARD_CHARACTER_DETAIL: '/dashboard/characters/:id',
  DASHBOARD_PACKAGES: '/dashboard/packages',
  DASHBOARD_PACKAGE_NEW: '/dashboard/packages/new',
  DASHBOARD_PACKAGE: '/dashboard/packages/:packageId',
  DASHBOARD_STORIES: '/dashboard/stories',
  DASHBOARD_STORY_NEW: '/dashboard/stories/new',
  DASHBOARD_STORY: '/dashboard/stories/:storyId',
  DASHBOARD_STORY_EPISODES: '/dashboard/stories/:storyId/episodes',
  DASHBOARD_STORY_EPISODE: '/dashboard/stories/:storyId/episodes/:episodeId',
  DASHBOARD_ADS: '/dashboard/ads',
  DASHBOARD_AD_PROJECT: '/dashboard/ads/:projectId',
  DASHBOARD_SLIDESHOWS: '/dashboard/slideshows',
  DASHBOARD_SLIDESHOWS_SETTINGS: '/dashboard/slideshows/settings',
  DASHBOARD_SLIDESHOW: '/dashboard/slideshows/:slideshowId',
  DASHBOARD_SLIDESHOW_PUSHES: '/dashboard/slideshows/pushes',
  DASHBOARD_SLIDESHOW_PUSH_NEW: '/dashboard/slideshows/pushes/new',
  DASHBOARD_SLIDESHOW_PUSH: '/dashboard/slideshows/pushes/:pushId',
  DASHBOARD_CAROUSELS: '/dashboard/carousels',
  DASHBOARD_CAROUSEL: '/dashboard/carousels/:carouselId',
  DASHBOARD_LORAS: '/dashboard/loras',
  DASHBOARD_TEAM: '/dashboard/team',
  DASHBOARD_API_KEYS: '/dashboard/api-keys',
  DASHBOARD_WEBHOOKS: '/dashboard/webhooks',
  DASHBOARD_USAGE: '/dashboard/usage',
  DASHBOARD_BILLING: '/dashboard/billing',

  // Legacy routes — redirect to dashboard (removed in Step 6)
  MODEL: '/dashboard',
  LOOK_LIKE: '/dashboard',
  CREATE: '/dashboard',
  IMAGE_STUDIO: '/dashboard/playground',
  VIDEO_STUDIO: '/dashboard',
  SPEECH_GENERATION: '/dashboard',
  UGC_TALK_GENERATION: '/dashboard',
  PROMPT_LIBRARY: '/dashboard',
  IMAGE_EDITOR: '/dashboard',
  STAGE_DESIGNER: '/dashboard',

  // Auth routes
  AUTH: {
    BASE: '/auth',
    REGISTER: '/auth/register',
  },

  // Admin routes (platform_admin only)
  ADMIN_TEAMS: '/admin/teams',
  ADMIN_USERS: '/admin/users',
  ADMIN_LORAS: '/admin/loras',
  ADMIN_LORA_DETAIL: '/admin/loras/:id',
  ADMIN_ACTIONS: '/admin/actions',
  ADMIN_ACTION_DETAIL: '/admin/actions/:id',
  ADMIN_CHARACTERS: '/admin/characters',
  ADMIN_STORIES: '/admin/stories',
  ADMIN_CHARACTER_SETTINGS: '/admin/character-settings',
  ADMIN_AUDIT: '/admin/audit',
  ADMIN_USAGE: '/admin/usage',
  ADMIN_INVOICES: '/admin/invoices',
  ADMIN_PRICING: '/admin/pricing',
  // LLM admin (Tasks 16/17/18) — list, create + detail, anchor knob.
  ADMIN_LLM_MODELS: '/admin/models',
  ADMIN_LLM_MODEL_NEW: '/admin/models/new',
  ADMIN_LLM_MODEL_DETAIL: '/admin/models/:id',
  ADMIN_LLM_ANCHOR: '/admin/pricing/anchor',
  ADMIN_TEMPLATES: '/admin/templates',
  ADMIN_BUNDLES: '/admin/bundles',
  ADMIN_MONITORING: '/admin/monitoring',
  ADMIN_DISPATCHER: '/admin/dispatcher',
  ADMIN_INFERENCES: '/admin/inferences',
  ADMIN_STORAGE_HEALTH: '/admin/storage-health',
  ADMIN_MODERATION: '/admin/moderation',

  // Onboarding
  ONBOARDING: '/onboarding',

  // Pending — closed-beta holding page for non-approved accounts.
  // ApprovedAccountGuard / ProtectedRoute redirects here when the user's
  // accountStatus is anything other than 'approved' (pending/rejected/suspended).
  PENDING: '/pending',

  // Invite
  INVITE: '/invite',

  // Dynamic routes (legacy, will be removed in Step 6)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  KONTEXT: (_kontextType: string) => `/dashboard`,

  // Utility routes
  NOT_FOUND: '*',
} as const;

// Type for route values
export type RouteValue = (typeof ROUTES)[keyof typeof ROUTES] | string;

/** Billing — `/dashboard/billing`. */
export interface BillingLinkParams {
  tab?: 'overview' | 'plans' | 'activity';
  /** Open the status view for this payment. Where Inqud's return URL lands. */
  payment?: number;
}

function withQuery(path: string, params?: Readonly<Record<string, string | undefined>>): string {
  if (!params) return path;
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value) query.set(key, value);
  }
  const search = query.toString();
  return search ? `${path}?${search}` : path;
}

/** Typed deep links: the parameter names live once, and a typo is a type error. */
export const LINK = {
  billing: (params?: BillingLinkParams) =>
    withQuery(ROUTES.DASHBOARD_BILLING, {
      tab: params?.tab,
      payment: params?.payment != null ? String(params.payment) : undefined,
    }),
} as const;
